import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Tabs, LoadingSpinner, Select, useToastActions, useDataTableUrl } from '@airclou/ui';
import { getAppRoleOptionsWithInherit, getServiceUrl } from '@airclou/utils';
import { Briefcase, ArrowLeft, Users, AppWindow } from 'lucide-react';
import { getWorkspace, listWorkspaceUsers, listWorkspaceApps, listWorkspaceGroups, removeWorkspaceUser, removeWorkspaceApp, updateWorkspaceAppDefaultRole, type Workspace, type WorkspaceUser, type WorkspaceApp, type WorkspaceGroup } from '@/api/workspaces';
import { getCompany } from '@/api/companies';
import { AddUserToWorkspaceModal } from '../../components/hierarchy/AddUserToWorkspaceModal';
import { AddAppToWorkspaceModal } from '../../components/hierarchy/AddAppToWorkspaceModal';
import { AddGroupToWorkspaceModal } from '../../components/hierarchy/AddGroupToWorkspaceModal';
import { isRecordGone } from '@/api/errors';
import { ErrorState } from '../../components/state/ErrorState';
import { RefreshErrorBanner } from '../../components/state/RefreshErrorBanner';
import { WorkspaceUsersTab } from '../../components/hierarchy/WorkspaceUsersTab';
import { WorkspaceAppsTab } from '../../components/hierarchy/WorkspaceAppsTab';
import { WorkspaceGroupsTab } from '../../components/hierarchy/WorkspaceGroupsTab';
import { WorkspaceSettingsTab } from '../../components/hierarchy/WorkspaceSettingsTab';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'

export function WorkspaceDetailsPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  // Adding and removing workspace members, apps and groups is `MANAGE` on the
  // API as of 2026-09-13 (role plan §6.4).
  const { user } = useAuth();
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [apps, setApps] = useState<WorkspaceApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The workspace is gone — deleted, or not this tenant's. A retry cannot bring
  // it back, so the page has nothing left to show even while it still holds the
  // rows it loaded. Only a successful load clears it.
  const [gone, setGone] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isAddAppModalOpen, setIsAddAppModalOpen] = useState(false);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  // Sort lives in the URL (same pattern as the audit log). One set of params
  // serves all three tables: only the active tab is mounted, and switching tabs
  // navigates to a new path, which drops the previous tab's sort.
  const { sorting, onSortingChange } = useDataTableUrl();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [updatingAppRole, setUpdatingAppRole] = useState<string | null>(null);
  const { success, error: toastError } = useToastActions();

  // Which load is the current one, and which workspace the page is showing.
  // `/workspaces/:id` stays mounted across a workspace switch — a URL edit, a
  // bookmark, back/forward — so an answer for the workspace we left can still be
  // in flight. Both have to agree: the id alone lets a refresh that started after
  // the switch win over the live load, and the sequence alone lets such a caller
  // claim the newest number and then fail its own id check, orphaning the live
  // load and leaving `loading` set with nothing to clear it.
  // The ref is written from the id-change effect, not during render, so a render
  // React abandons cannot publish an id that never mounted.
  const loadSeq = useRef(0);
  const shownId = useRef(id);

  const stillShowing = (loadId?: string) => loadId === shownId.current;
  const isCurrent = (seq: number, loadId?: string) =>
    seq === loadSeq.current && stillShowing(loadId);

  const loadData = async () => {
    if (!id) return;

    // Bail before claiming a sequence number: a stale caller that took the newest
    // one and then failed its own id check would orphan the live load.
    if (!stillShowing(id)) return;

    const seq = ++loadSeq.current;
    const loadId = id;
    try {
      setLoading(true);
      // `error` is cleared on success rather than here: the retry affordance sits
      // inside the banner and the error state, so clearing it up front unmounts
      // the thing the operator just clicked and the retry reads as dead.

      // One round of requests rather than workspace-then-lists: the tabs are
      // mounted from the first paint now, so there is nothing to gain by making
      // the lists wait for the header's row.
      const [workspaceResponse, usersResponse, groupsResponse, appsResponse] = await Promise.all([
        getWorkspace(id),
        listWorkspaceUsers(id),
        listWorkspaceGroups(id),
        listWorkspaceApps(id),
      ]);
      if (!isCurrent(seq, loadId)) return;

      setWorkspace(workspaceResponse.workspace);
      setUsers(usersResponse.users);
      setGroups(groupsResponse.groups);
      setApps(appsResponse.apps);
      setGone(false);
      setError(null);

      // The company name only labels the back button, so its failure must not
      // take the page down with it — the label falls back to "Back to company"
      // and the workspace's own failure path covers the resource that matters.
      // Deliberately not awaited: nothing below it needs the name.
      getCompany(workspaceResponse.workspace.company_id)
        .then(companyResponse => {
          if (isCurrent(seq, loadId)) setCompanyName(companyResponse.company.name);
        })
        .catch(err => {
          // Logged rather than surfaced: the operator loses a name on a button,
          // not access to anything. A trace still beats no record at all.
          console.error('Failed to load company name for back link:', err);
          if (isCurrent(seq, loadId)) setCompanyName('');
        });
    } catch (err: any) {
      if (!isCurrent(seq, loadId)) return;
      // Latches: only a successful load clears it, so a blip on the retry cannot
      // put a deleted workspace — and its editable settings form — back.
      if (isRecordGone(err)) setGone(true);
      setError(err.message || t('workspaces.failedToLoad'));
    } finally {
      if (isCurrent(seq, loadId)) setLoading(false);
    }
  };

  // Merge a row the server just returned (e.g. after a logo change) into our copy, so the
  // page updates without refetching. Merging rather than replacing keeps the fields the
  // update endpoint doesn't return (company_name, user_count, ...) that only getWorkspace supplies.
  const patchWorkspace = (patch: Partial<Workspace>) => {
    setWorkspace(prev => {
      if (!prev) return prev;
      // A logo upload or removal that resolves after a workspace switch hands up
      // the row it was for. Merging it would paint that workspace's logo onto the
      // one now on screen, and the settings tab is keyed by id so it cannot tell
      // us itself.
      if (patch.id && patch.id !== prev.id) return prev;
      return { ...prev, ...patch };
    });
  };

  useEffect(() => {
    shownId.current = id;
    // A different workspace under the same mounted route. Drop the previous one's
    // data first: nothing from workspace A may render — or be edited — under
    // workspace B's id, and the settings form has to go with it.
    setWorkspace(null);
    setCompanyName('');
    setUsers([]);
    setGroups([]);
    setApps([]);
    // Both belong to the workspace we were showing; a different id has not
    // answered yet.
    setGone(false);
    setError(null);
    // Describes *this page* saving, so it must not carry over.
    setUpdatingAppRole(null);
    loadData();
  }, [id]);

  useEffect(() => {
    const path = location.pathname.replace(/\/$/, ''); // Remove trailing slash if present

    if (path.endsWith('/applications')) {
      setActiveTab('applications');
      return;
    }

    if (path.endsWith('/groups')) {
      setActiveTab('groups');
      return;
    }

    if (path.endsWith('/users')) {
      setActiveTab('users');
      return;
    }

    if (path.endsWith('/settings')) {
      navigate(`/workspaces/${id}/overview`, { replace: true });
      return;
    }

    if (path.endsWith('/overview')) {
      setActiveTab('overview');
      return;
    }

    // Default for the base details route: redirect to "overview" so each tab has a canonical URL.
    if (id && path.endsWith(`/workspaces/${id}`)) {
      setActiveTab('overview'); // Ensure activeTab is set before navigation to prevent flicker or race condition
      navigate(`/workspaces/${id}/overview`, { replace: true });
    }
  }, [id, location.pathname, navigate]);

  const handleRemoveUser = async (userId: string) => {
    if (!workspace) return;
    
    try {
      const target = workspace.id;
      await removeWorkspaceUser(target, userId);
      success(t('workspaces.memberRemoved'), t('common.success'));
      // This handler's `loadData` is the closure from the render that showed
      // `target`, so after a switch it would bail on its own id check anyway —
      // this just stops the pointless call. The load-bearing guard is inside
      // `loadData`.
      if (stillShowing(target)) loadData();
    } catch (err: any) {
      toastError(err.message || t('workspaces.failedToRemoveMember'), t('common.error'));
    }
  };

  const handleRemoveApp = async (appId: string) => {
    if (!workspace) return;
    
    try {
      const target = workspace.id;
      await removeWorkspaceApp(target, appId);
      success(t('workspaces.appRemoved'), t('common.success'));
      // Same as above: the bail inside `loadData` is what actually protects this.
      if (stillShowing(target)) loadData();
    } catch (err: any) {
      toastError(err.message || t('workspaces.failedToRemoveApp'), t('common.error'));
    }
  };

  const handleUpdateDefaultRole = async (appId: string, role: string) => {
    if (!workspace) return;
    
    setUpdatingAppRole(appId);
    try {
      // Use null to clear the role, otherwise use the role value
      const roleValue = role === '' ? null : role;
      const target = workspace.id;
      await updateWorkspaceAppDefaultRole(target, appId, roleValue);

      // Only onto the workspace the change was for: after a switch this would
      // stamp the new role onto the app list of whichever workspace is on screen.
      if (stillShowing(target)) {
        setApps(prev => prev.map(app =>
          app.app_id === appId
            ? { ...app, default_member_role: roleValue }
            : app
        ));
      }

      success(t('workspaces.defaultRoleUpdated'), t('common.success'));
    } catch (err: any) {
      toastError(err.message || t('workspaces.failedToUpdateRole'), t('common.error'));
    } finally {
      setUpdatingAppRole(null);
    }
  };

  const userColumns: any[] = [
    {
      id: 'email',
      header: t('common.member'),
      size: 280,
      enableSorting: false,
      cell: (user: WorkspaceUser) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/15 dark:bg-primary/25">
            <Users className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-foreground">
              {user.user?.name || user.user?.email || user.user_id}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {user.user?.email && user.user?.name ? user.user.email : user.role}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'role',
      header: t('common.role'),
      size: 100,
      accessorKey: 'role',
      enableSorting: true,
      cell: (user: WorkspaceUser) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          user.role === 'admin' 
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
        }`}>
          {user.role === 'admin' ? t('common.admin') : t('common.member')}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: t('common.added'),
      size: 120,
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (user: WorkspaceUser) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(user.created_at, timezone)}
        </div>
      ),
    },
    // Dropped rather than emptied — a column reserves its width either way.
    ...(canManage ? [{
      id: 'actions',
      header: '',
      size: 100,
      enableSorting: false,
      cell: (user: WorkspaceUser) => (
        <div className="flex items-center justify-end gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              handleRemoveUser(user.user_id);
            }}
          >
            {t('common.remove')}
          </Button>
        </div>
      ),
    }] : []),
  ];

  const appColumns: any[] = [
    {
      id: 'app_name',
      header: t('workspaces.application'),
      cell: (app: WorkspaceApp) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/15 dark:bg-primary/25">
            <AppWindow className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="font-medium text-foreground">
              {app.app?.name || app.app_id}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {app.app?.slug || app.app_id}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'enabled_at',
      header: t('common.added'),
      cell: (app: WorkspaceApp) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(app.enabled_at, timezone)}
        </div>
      ),
    },
    {
      id: 'default_member_role',
      header: t('workspaces.defaultMemberRole'),
      cell: (app: WorkspaceApp) => (
        <div className="w-40">
          <Select
            value={app.default_member_role || ''}
            onChange={(value) => handleUpdateDefaultRole(app.app_id, value)}
            // Writes `PATCH /api/workspaces/:id/apps/:app_id`, which is MANAGE.
            // Left enabled, a viewer could pick a role and get a 403 toast with
            // the row snapping back — the Remove button in the next column over
            // is hidden from them, so this one staying live read as an
            // oversight, which it was.
            disabled={updatingAppRole === app.app_id || !canManage}
            options={getAppRoleOptionsWithInherit(app.app?.slug)}
            placeholder={t('common.selectRole')}
            minWidth="140px"
          />
        </div>
      ),
    },
    ...(canManage ? [{
      id: 'actions',
      header: '',
      cell: (app: WorkspaceApp) => (
        <div className="flex items-center justify-end gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => handleRemoveApp(app.app_id)}
          >
            {t('common.remove')}
          </Button>
        </div>
      ),
    }] : []),
  ];

  // Filter to show only direct members (not group-based) in the Users tab
  // Group-based members are shown in the Groups tab
  const directUsers = users.filter(u => u.access_type !== 'group');


  // `gone` stands on its own; otherwise the error page is only for "there is no
  // workspace to show". Keyed off `error` as well as `!loading` so it survives its
  // own retry instead of flashing the skeleton shell mid-flight.
  if (gone || (!workspace && (error || !loading))) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/companies')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <ErrorState
          title={t('workspaces.failedToLoad')}
          description={gone || !error ? t('workspaces.workspaceNotFound') : error}
          onRetry={loadData}
          isRetrying={loading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => workspace ? navigate(`/companies/${workspace.company_id}`) : navigate('/companies')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {companyName ? t('workspaces.backToCompany', { name: companyName }) : t('workspaces.backToCompanyDefault')}
        </Button>

        <div className="flex items-start justify-between" aria-busy={!workspace}>
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-primary/15 dark:bg-primary/25 overflow-hidden">
              {workspace?.logo_url ? (
                <img
                  src={workspace.logo_url.startsWith('http') ? workspace.logo_url : `${getServiceUrl('dashboard')}${workspace.logo_url}`}
                  alt={`${workspace.name} logo`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Briefcase className="w-8 h-8 text-primary" />
              )}
            </div>
            <div>
              {workspace ? (
                <>
                  <h1 className="text-2xl font-bold text-foreground">{workspace.name}</h1>
                  <p className="mt-1 text-sm text-muted-foreground">{workspace.slug}</p>
                </>
              ) : (
                <>
                  <div className="h-8 w-48 animate-pulse rounded bg-muted" />
                  <div className="mt-2 h-4 w-24 animate-pulse rounded bg-muted" />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* A reload that failed while the page still holds good rows: keep the page
          and offer a retry. A "workspace is gone" answer took the early return
          above instead. */}
      {error && workspace && (
        <RefreshErrorBanner message={error} onRetry={loadData} isRetrying={loading} />
      )}

      {/* Tabs — the strip is part of the shell, so it renders immediately and each
          tab gates only its own data region. Keyed by workspace: the settings form
          holds per-workspace state that must not carry across a switch. */}
      <>
          <Tabs
            key={id}
            tabs={[
              {
                id: 'overview',
                label: t('common.overview'),
                content: workspace ? (
                  <WorkspaceSettingsTab
                    workspace={workspace}
                    onUpdate={() => loadData()}
                    onWorkspaceChange={patchWorkspace}
                  />
                ) : (
                  <div className="flex min-h-[300px] items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ),
              },
              {
                // The count waits for the rows: "Members (0)" on a workspace that
                // has members reads as an answer, not as a page still loading.
                id: 'users',
                label: workspace ? `${t('common.members')} (${directUsers.length})` : t('common.members'),
                content: (
                  <WorkspaceUsersTab
                    users={directUsers}
                    columns={userColumns}
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    getRowHref={(user) => `/workspaces/${id}/users/${user.user_id}`}
                    onAddUser={() => setIsAddUserModalOpen(true)}
                    isLoading={loading}
                  />
                ),
              },
              {
                id: 'groups',
                label: workspace ? `${t('common.groups')} (${groups.length})` : t('common.groups'),
                content: (
                  <WorkspaceGroupsTab
                    workspaceId={id!}
                    groups={groups}
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    onAddGroup={() => setIsAddGroupModalOpen(true)}
                    onGroupRemoved={() => loadData()}
                    isLoading={loading}
                  />
                ),
              },
              {
                id: 'applications',
                label: t('common.applications'),
                content: (
                  <WorkspaceAppsTab
                    apps={apps}
                    columns={appColumns}
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    onAddApp={() => setIsAddAppModalOpen(true)}
                    isLoading={loading}
                  />
                ),
              },
            ]}
            activeTab={activeTab === 'settings' ? 'overview' : activeTab}
            onTabChange={(tabId) => {
              setActiveTab(tabId);
              if (!id) return;
              navigate(`/workspaces/${id}/${tabId}`);
            }}
          />

          {/* Modals */}
          {isAddUserModalOpen && (
            <AddUserToWorkspaceModal
              workspaceId={id!}
              isOpen={isAddUserModalOpen}
              onClose={() => setIsAddUserModalOpen(false)}
              onSuccess={() => {
                setIsAddUserModalOpen(false);
                loadData();
              }}
              existingUserIds={users.map(u => u.user_id)}
            />
          )}

          {isAddAppModalOpen && (
            <AddAppToWorkspaceModal
              workspaceId={id!}
              isOpen={isAddAppModalOpen}
              onClose={() => setIsAddAppModalOpen(false)}
              onSuccess={() => {
                setIsAddAppModalOpen(false);
                loadData();
              }}
            />
          )}

          {isAddGroupModalOpen && (
            <AddGroupToWorkspaceModal
              workspaceId={id!}
              isOpen={isAddGroupModalOpen}
              onClose={() => setIsAddGroupModalOpen(false)}
              onSuccess={() => {
                setIsAddGroupModalOpen(false);
                loadData();
              }}
            />
          )}
      </>
    </div>
  );
}
